import { useMemo } from "react";
import { SelectedData } from "../types";

interface SelectedDataInfoProps {
    selectedItems: SelectedData[]
}

interface Kit {
    isSelected?: boolean;
    data: SelectedData;
    components?: SelectedData[]
}

export type HierachyType = { isSelected?: boolean; data: SelectedData; products?: SelectedData[]; kits?: Record<number, Kit>; }

const useGetSelectedDataInfo = ({ selectedItems }: SelectedDataInfoProps) => {
    const data = useMemo(() => {
        const hierarchy: Record<number, HierachyType> = {};
        let completelySelectedSectionIds: number[] = [];
        let partiallySelectedSectionIds: number[] = [];
        const partiallySelectedKitIds: number[] = [];
        const completelySelectedKitIdsFromCompleteSections: number[] = [];
        const completelySelectedKitIdsFromPartialSections: number[] = [];
        let productsFromPartialSections: SelectedData[] = [];
        let productsFromCompleteSections: SelectedData[] = [];
        let componentsFromPartialKits: SelectedData[] = [];
        let componentsFromCompleteKits: SelectedData[] = [];
        let text: string[] = [];

        if ((selectedItems || []).length) {
            //creating a hierarchy of selected items in order of {sectionId => {products, kits}}
            (selectedItems || []).forEach((item) => {
                const sectionId = item?.groupPath[0];
                if (item?.groupPath.length === 1) {
                    hierarchy[item.id] = { isSelected: true, data: item, products: hierarchy[item.id]?.products || [], kits: hierarchy[item.id]?.kits || [] };
                }
                if (item?.groupPath.length === 2) {
                    if (item.is_kit) {
                        hierarchy[sectionId] = {
                            ...(hierarchy?.[sectionId] || {}),
                            kits: {
                                ...(hierarchy?.[sectionId]?.kits || {}),
                                [item.id]: {
                                    ...(hierarchy?.[sectionId]?.kits?.[item.id] || {}),
                                    isSelected: true,
                                    data: item
                                }
                            }
                        };
                    } else {
                        hierarchy[sectionId] = {
                            ...(hierarchy?.[sectionId] || {}),
                            products: [...(hierarchy?.[sectionId]?.products || []), item]
                        };
                    }
                }
                if (item?.groupPath.length === 3) {
                    const kitId = item?.groupPath[1];
                    hierarchy[sectionId] = {
                        ...(hierarchy?.[sectionId] || {}),
                        kits: {
                            ...(hierarchy?.[sectionId]?.kits || {}),
                            [kitId]: {
                                ...(hierarchy?.[sectionId]?.kits?.[kitId] || {}),
                                components: [...(hierarchy?.[sectionId]?.kits?.[kitId]?.components || []), item]
                            }
                        } as any
                    };
                }
            });
        }

        //counting the products, kits, components from completely and partially selected sections. 
        Object.keys(hierarchy).forEach((sectionId) => {
            const sectionData = hierarchy[Number(sectionId)];
            const isSectionCompletelySelected = sectionData.isSelected;
            if (!isSectionCompletelySelected) {
                partiallySelectedSectionIds.push(sectionData?.data?.id);
                productsFromPartialSections.push(...(sectionData.products || []))
            } else {
                completelySelectedSectionIds.push(sectionData?.data?.id);
                productsFromCompleteSections.push(...(sectionData.products || []))
            }
            Object.keys(sectionData.kits || {}).forEach((kitId) => {
                const kitData = sectionData.kits?.[Number(kitId)];
                if (!kitData?.isSelected) {
                    partiallySelectedKitIds.push(Number(kitId));
                    componentsFromPartialKits.push(...(kitData?.components || []))
                } else {
                    if (!isSectionCompletelySelected) {
                        completelySelectedKitIdsFromPartialSections.push(Number(kitId));
                    } else {
                        completelySelectedKitIdsFromCompleteSections.push(Number(kitId));
                    }
                    componentsFromCompleteKits.push(...(kitData.components || []))
                }
            });
        });

        if (completelySelectedSectionIds.length) {
            text.push(`${completelySelectedSectionIds.length} section(s)`)
        }
        if (productsFromPartialSections.length) {
            text.push(`${productsFromPartialSections.length} product(s)`)
        }
        if (completelySelectedKitIdsFromPartialSections.length) {
            text.push(`${completelySelectedKitIdsFromPartialSections.length} kit(s)`)
        }
        if (componentsFromPartialKits.length) {
            text.push(`${componentsFromPartialKits.length} component(s)`)
        }
        return { hierarchy, completelySelectedSectionIds, partiallySelectedSectionIds, partiallySelectedKitIds, completelySelectedKitIdsFromCompleteSections, completelySelectedKitIdsFromPartialSections, productsFromPartialSections, productsFromCompleteSections, componentsFromPartialKits, componentsFromCompleteKits, selectedText: text.join(', ') + ' selected' }
    }, [selectedItems]);

    const { hierarchy, completelySelectedSectionIds, partiallySelectedSectionIds, partiallySelectedKitIds, completelySelectedKitIdsFromCompleteSections, completelySelectedKitIdsFromPartialSections, productsFromPartialSections, productsFromCompleteSections, componentsFromPartialKits, componentsFromCompleteKits, selectedText } = data;

    const isItemsFromDifferentSections = (completelySelectedSectionIds.length + partiallySelectedSectionIds.length) > 1;
    const isKitSelected = (completelySelectedKitIdsFromPartialSections.length + completelySelectedKitIdsFromCompleteSections.length + partiallySelectedKitIds.length) > 0;
    const componentIdsFromPartialKits = componentsFromPartialKits.map((item) => item.id);
    const componentIdsFromCompleteKits = componentsFromCompleteKits.map((item) => item.id);
    const productsIdsFromPartialSections = productsFromPartialSections.map((item) => item.id);
    const productIdsFromCompleteSections = productsFromCompleteSections.map((item) => item.id);
    const componentIds = [...componentIdsFromPartialKits, ...componentIdsFromCompleteKits];
    const isComponentsSelected = componentIds.length > 0;
    const sectionIds = [...partiallySelectedSectionIds, ...completelySelectedSectionIds];
    const kitIds = [...partiallySelectedKitIds, ...completelySelectedKitIdsFromCompleteSections, ...completelySelectedKitIdsFromPartialSections];
    const productIds = [...productsFromPartialSections, ...productsFromCompleteSections].sort((a, b) => a.order - b.order).map((item) => item.id);
    return {
        hierarchy,
        isItemsFromDifferentSections,
        isKitSelected,
        isComponentsSelected,
        productIds,
        kitIds,
        sectionIds,
        componentIds,
        componentIdsFromPartialKits,
        partiallySelectedKitIds,
        productsIdsFromPartialSections,
        productIdsFromCompleteSections,
        completelySelectedSectionIds,
        completelySelectedKitIdsFromPartialSections,
        completelySelectedKitIdsFromCompleteSections,
        isCreateKitDisabled: isItemsFromDifferentSections || isKitSelected || isComponentsSelected,
        isChangeSectionDisabled: isItemsFromDifferentSections || (!(productIds.length + completelySelectedKitIdsFromCompleteSections.length + completelySelectedKitIdsFromPartialSections.length) && partiallySelectedKitIds.length > 0),
        selectedText,
        componentsFromPartialKits,
        productsFromCompleteSections,
        productsFromPartialSections
    };
}

export default useGetSelectedDataInfo;